/**
 * Theme color — цвет системной полосы над вебвью.
 *
 * Это `<meta name="theme-color">`, и красит он две разные вещи одним значением: полосу
 * статус-бара в установленном приложении и адресную строку браузера на телефоне. Ни одна из них
 * не является элементом вёрстки — CSS до них не достаёт, поэтому цвет приходится объявлять
 * тегом и менять из JS.
 *
 * **Почему цвет — свойство ЭКРАНА, а не приложения.** Правило владельца, 01.09.2026: «должен быть
 * или белый, или серый в зависимости от страницы — на главной белый, так как там сверху панель, а
 * страница чата и профиль серый должен быть». Полоса продолжает то, что под ней; на «Главной» под
 * ней белая панель `ClientSectionAppBar`, на профиле — канва. Какой экран какую носит, знает
 * `clientPhoneStatusBarSurface` в таблице маршрутов кабинета — здесь только механика.
 *
 * **Почему значение читается из вычисленного CSS, а не пишется числом.** Заказчик выбирает фон в
 * «Профиль → Оформление», и `:root[data-app-bg="custom"]` (`index.css`) пересчитывает через
 * `color-mix()` ОБЕ поверхности — и канву, и карточку. Литерал `#F6F6F7` разошёлся бы с экраном
 * на первом же пресете, ровно как `--client-canvas` расходился с канвой до 27.08.2026, когда был
 * литералом. Поэтому {@link applyThemeColor} спрашивает у документа, а
 * {@link refreshThemeColor} зовётся после каждой смены фона.
 *
 * Спрашивать при этом приходится хитро: сырое значение переменной — это и есть `color-mix(…)`,
 * которое в теге не цвет. Как из него получается `rgb()`, написано у {@link probeColor}.
 *
 * **Почему на iOS этого может быть недостаточно.** `theme-color` Safari понимает с 15-й версии,
 * но `client.html` объявляет ещё и `apple-mobile-web-app-status-bar-style: default` — старый
 * механизм Apple, который в standalone может забирать приоритет себе и красить полосу белым
 * независимо от этого модуля. Порядок разбирательства записан в спеке PWA кабинета: сперва
 * смотрим на устройстве с метой, потом без неё. На `black-translucent` не переходить — он делает
 * иконки статус-бара белыми, а канва светлая.
 */

// ============================================================================
// Constants
// ============================================================================

/** Токены двух поверхностей, между которыми переключается полоса. */
const SURFACE_VAR = {
  card: '--color-bg-card',
  canvas: '--color-bg-app',
} as const;

export type ThemeColorSurface = keyof typeof SURFACE_VAR;

/**
 * Запасное значение на случай, когда переменная не прочиталась.
 *
 * Совпадает с `--color-bg-app` в `index.css` и с `DEFAULT_BG` в `appBackground.ts`. Это четвёртое
 * место с тем же числом, и оно тут не по небрежности: сюда попадают только среды, где
 * `getComputedStyle` вернул пустую строку (jsdom без стилей), — то есть там, где сверять уже не с
 * чем.
 */
const FALLBACK = '#f6f6f7';

// ============================================================================
// Internals
// ============================================================================

/** Тег `theme-color` документа; создаётся, если разметка его не объявила. */
function metaElement(): HTMLMetaElement | null {
  if (typeof document === 'undefined') return null;

  const existing = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (existing) return existing;

  const created = document.createElement('meta');
  created.name = 'theme-color';
  document.head.appendChild(created);
  return created;
}

/**
 * Цвет переменной, РАЗРЕШЁННЫЙ до `rgb()`, а не её сырое значение.
 *
 * Разница не теоретическая, и она стоила бы всей затеи. Кастомные свойства вычисляются лениво:
 * при выбранном пресете фона `getComputedStyle(root).getPropertyValue('--color-bg-card')`
 * возвращает буквально `color-mix(in srgb, #f4f2ef 12%, white)` — строку с функцией. В
 * `<meta name="theme-color">` такое не цвет: браузер его не разбирает и оставляет полосу
 * прежней. Поймано на стенде 01.09.2026, до этого правка молча не работала на всех пресетах,
 * кроме стандартного.
 *
 * Разрешает браузер: элементу без размеров задаётся `background-color: var(--…)`, и обратно
 * читается уже посчитанный цвет, который {@link toHex} приводит к `#rrggbb`. Стоит одного
 * reflow — на смену экрана, то есть примерно никогда.
 */
/**
 * Приводит посчитанный браузером цвет к `#rrggbb`.
 *
 * Форматов на входе два, и второй — сюрприз стенда: `rgb(246, 246, 247)` для обычной переменной
 * и `color(srgb 0.99 0.99 0.99)` для той, что посчитана через `color-mix()` (Chrome отдаёт
 * результат смешивания именно так). Оба — валидный CSS, но `<meta name="theme-color">` читают
 * очень разные движки, включая давние сборки WebKit на телефонах, и hex понимают все.
 *
 * `null` — «это не цвет»: подстановки не было вовсе (jsdom возвращает `var(--…)` буквально).
 */
function toHex(computed: string): string | null {
  const channel = (value: number): string =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');

  const rgb = /^rgba?\(([^)]+)\)$/.exec(computed);
  if (rgb) {
    const [r, g, b] = rgb[1].split(/[\s,/]+/).filter(Boolean).map(Number);
    if ([r, g, b].some(Number.isNaN)) return null;
    return `#${channel(r)}${channel(g)}${channel(b)}`;
  }

  // Каналы здесь в долях единицы, а не в 0…255.
  const srgb = /^color\(srgb\s+([^)]+)\)$/.exec(computed);
  if (srgb) {
    const [r, g, b] = srgb[1].split(/[\s/]+/).filter(Boolean).map(Number);
    if ([r, g, b].some(Number.isNaN)) return null;
    return `#${channel(r * 255)}${channel(g * 255)}${channel(b * 255)}`;
  }

  return null;
}

function probeColor(name: string): string | null {
  if (typeof document === 'undefined' || !document.body) return null;

  const probe = document.createElement('span');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.cssText = 'position:absolute;width:0;height:0;opacity:0;pointer-events:none';
  probe.style.backgroundColor = `var(${name})`;
  document.body.appendChild(probe);

  const computed = getComputedStyle(probe).backgroundColor;
  probe.remove();

  // Прозрачное — признак того, что переменной нет: ни одна из наших поверхностей прозрачной не
  // бывает. Остальное отбраковывает `toHex`, если это вообще не цвет.
  if (computed === 'rgba(0, 0, 0, 0)' || computed === 'transparent') return null;
  return toHex(computed);
}

/**
 * Значение CSS-переменной с `<html>`, или `null`.
 *
 * Запасной путь для сред, которые `var()` не вычисляют вовсе (jsdom). Значение с функцией
 * внутри отбрасывается: в теге оно бесполезно ровно так же, как `color-mix` выше.
 */
function readVar(name: string): string | null {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return null;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  if (value.length === 0 || value.includes('(')) return null;
  return value;
}

/** Цвет поверхности: сперва разрешённый браузером, затем сырая переменная, затем умолчание. */
function surfaceColor(name: string): string {
  return probeColor(name) ?? readVar(name) ?? FALLBACK;
}

// ============================================================================
// API
// ============================================================================

/**
 * Красит полосу в поверхность `surface` и запоминает выбор, чтобы {@link refreshThemeColor} мог
 * пересчитать его после смены фона.
 *
 * Возвращает фактически применённый цвет — тестам и вызывающему проще проверить его, чем лезть в
 * `document.head`.
 */
export function applyThemeColor(surface: ThemeColorSurface): string {
  currentSurface = surface;
  const color = surfaceColor(SURFACE_VAR[surface]);
  const meta = metaElement();
  if (meta) meta.content = color;
  return color;
}

/**
 * Поверхность, выбранная последним {@link applyThemeColor}, или `null`, пока никто не выбирал.
 *
 * Модульная переменная, а не React-состояние: тег в документе один, и владелец у него должен быть
 * один — иначе две шапки в переходе между экранами подерутся за один `<meta>`.
 */
let currentSurface: ThemeColorSurface | null = null;

/**
 * Пересчитывает полосу под текущий фон приложения. Зовётся после `applyBackground()`: цвет
 * поверхности изменился, а экран остался прежним.
 *
 * Ничего не делает, пока полосу никто не занял — во внутреннем приложении, например, где
 * `theme-color` остаётся брендовым из разметки.
 */
export function refreshThemeColor(): void {
  if (currentSurface === null) return;
  applyThemeColor(currentSurface);
}

/**
 * Возвращает полосу к значению, объявленному в разметке. Зовётся при уходе с кабинета: внутреннее
 * приложение живёт в том же документе и своей полосы терять не должно.
 */
export function resetThemeColor(initial: string): void {
  currentSurface = null;
  const meta = metaElement();
  if (meta) meta.content = initial;
}

/** Текущее содержимое тега — чем полоса покрашена прямо сейчас. */
export function readThemeColor(): string {
  return metaElement()?.content ?? '';
}
